

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { io } from "socket.io-client";
import {
  FaPaperclip,
  FaSmile,
  FaPaperPlane,
  FaChevronLeft,
  FaUserCircle,
  FaHeadset,
} from "react-icons/fa";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import "./UserChatWidget.css"; // ← adjust path to wherever this file actually lives in your project

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const STORAGE_KEY = "support_chat_id";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const extractChatId = (data) => {
  if (!data) return null;
  const raw = data.chat?._id ?? data._id ?? data.chatId ?? null;
  if (!raw) return null;
  const str = String(raw);
  return str === "undefined" || str === "null" ? null : str;
};

const safePersist = (id) => {
  if (!id || id === "undefined" || id === "null") return null;
  localStorage.setItem(STORAGE_KEY, id);
  return id;
};

const clearPersisted = () => localStorage.removeItem(STORAGE_KEY);

// ─── TypingIndicator ──────────────────────────────────────────────────────────

const TypingIndicator = ({ name }) => (
  <div className="fc-msg-row fc-msg-row--agent">
    <div className="fc-avatar fc-avatar--agent">
      <FaUserCircle />
    </div>
    <div className="fc-bubble-wrap">
      <span className="fc-sender-name">{name || "Support"}</span>
      <div className="fc-bubble fc-bubble--agent fc-bubble--typing">
        <span />
        <span />
        <span />
      </div>
    </div>
  </div>
);

// ─── WelcomeScreen ────────────────────────────────────────────────────────────

// Kept in sync with tutorslinks-admin's HelpSupport.jsx CATEGORIES and the
// Chat model's enum (models/Chat.js) — change all three together.
const SUPPORT_CATEGORIES = [
  "Registration/Onboarding",
  "Profile Completion",
  "Login/Password",
  "KYC/Verification",
  "Role/Account Type",
  "Pricing",
  "Tutor Matching",
  "Student Enquiry",
  "Reviews",
  "Technical Error",
  "Payment",
  "Other",
];

const WelcomeScreen = ({ onStart, loading, firstName, category, onCategoryChange }) => (
  <div className="fc-welcome">
    <div className="fc-illustration">
      <svg viewBox="0 0 220 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="118" width="160" height="8" rx="4" fill="#e2e8f0" />
        <rect x="100" y="80" width="70" height="44" rx="4" fill="#cbd5e1" />
        <rect x="104" y="84" width="62" height="34" rx="2" fill="#1e3a5f" />
        <rect x="95" y="124" width="80" height="4" rx="2" fill="#94a3b8" />
        <ellipse
          cx="75"
          cy="130"
          rx="22"
          ry="12"
          fill="#fbbf24"
          opacity="0.3"
        />
        <circle cx="75" cy="78" r="18" fill="#fde68a" />
        <rect x="54" y="92" width="42" height="36" rx="8" fill="#f59e0b" />
        <path
          d="M60 74 Q75 58 90 74"
          stroke="#1e3a5f"
          strokeWidth="3"
          fill="none"
        />
        <circle cx="60" cy="77" r="5" fill="#1e3a5f" />
        <circle cx="90" cy="77" r="5" fill="#1e3a5f" />
        <rect x="87" y="77" width="8" height="12" rx="3" fill="#1e3a5f" />
        <rect x="118" y="30" width="72" height="36" rx="12" fill="#0ea5e9" />
        <polygon points="132,66 140,66 136,76" fill="#0ea5e9" />
        <circle cx="134" cy="48" r="4" fill="white" />
        <circle cx="148" cy="48" r="4" fill="white" />
        <circle cx="162" cy="48" r="4" fill="white" />
        <rect x="60" y="18" width="56" height="32" rx="10" fill="#e0f2fe" />
        <polygon points="88,50 96,50 90,60" fill="#e0f2fe" />
        <text x="88" y="38" textAnchor="middle" fontSize="18">
          ❓
        </text>
      </svg>
    </div>
    <div className="fc-welcome-text">
      <h4>Hi{firstName ? `, ${firstName}` : ""} 👋</h4>
      <p>Our support team is ready to help you with anything on TutorsLinks.</p>
    </div>
    <label className="fc-category-label">
      What's this about?
      <select
        className="fc-category-select"
        value={category}
        onChange={(e) => onCategoryChange(e.target.value)}
      >
        {SUPPORT_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </label>
    <button className="fc-start-btn" onClick={onStart} disabled={loading}>
      {loading ? "Connecting…" : "Start a Conversation"}
    </button>
  </div>
);

// ─── ChatScreen ───────────────────────────────────────────────────────────────

const ChatScreen = ({
  messages,
  onSend,
  onTyping,
  agentName,
  agentAvatar,
  userAvatar,
  agentTyping,
}) => {
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, agentTyping]);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
  };

  const grouped = messages.reduce((acc, msg) => {
    const label = new Date(msg.createdAt).toLocaleDateString([], {
      month: "long",
      day: "numeric",
    });
    (acc[label] = acc[label] || []).push(msg);
    return acc;
  }, {});

  return (
    <div className="fc-chat-screen">
      <div className="fc-messages">
        {Object.entries(grouped).map(([label, msgs]) => (
          <React.Fragment key={label}>
            <div className="fc-date-label">{label}</div>
            {msgs.map((msg) => {
              const isUser = msg.senderType === "user";
              return (
                <div
                  key={msg._id || msg.tempId}
                  className={`fc-msg-row ${isUser ? "fc-msg-row--user" : "fc-msg-row--agent"}`}
                >
                  {!isUser && (
                    <div className="fc-avatar fc-avatar--agent">
                      {agentAvatar ? (
                        <img src={agentAvatar} alt={agentName} />
                      ) : (
                        // <FaUserCircle />
                        <FaHeadset />
                      )}
                    </div>
                  )}
                  <div className="fc-bubble-wrap">
                    {!isUser && (
                      <span className="fc-sender-name">
                        {agentName || "Support"}
                      </span>
                    )}
                    <div
                      className={`fc-bubble ${isUser ? "fc-bubble--user" : "fc-bubble--agent"}`}
                    >
                      {msg.text}
                    </div>
                    <div
                      className={`fc-time ${isUser ? "fc-time--right" : ""}`}
                    >
                      {formatTime(msg.createdAt)}
                      {isUser && (
                        <span className="fc-tick">{msg.read ? "✓✓" : "✓"}</span>
                      )}
                    </div>
                  </div>
                  {isUser && (
                    <div className="fc-avatar fc-avatar--user">
                      {userAvatar ? (
                        <img src={userAvatar} alt="You" />
                      ) : (
                        <FaUserCircle />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}

        {agentTyping && <TypingIndicator name={agentName} />}
        <div ref={bottomRef} />
      </div>

      <div className="fc-input-bar">
        <button className="fc-icon-btn" title="Attach">
          <FaPaperclip />
        </button>
        <button className="fc-icon-btn" title="Emoji">
          <FaSmile />
        </button>
        <input
          className="fc-input"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            onTyping?.();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Type a message"
        />
        <button className="fc-send-btn" onClick={send} disabled={!text.trim()}>
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
};

// ─── UserChatWidget (main) ─────────────────────────────────────────────────────

const UserChatWidget = forwardRef(({ userAvatar }, ref) => {
  const { user, token: authToken } = useAuth();
  const token = authToken ?? localStorage.getItem("token");

  console.log("[UserChatWidget debug]", {
    user,
    token,
    hasUser: !!user,
    isAdmin: user?.isAdmin,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState("welcome");
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && stored !== "undefined" && stored !== "null"
      ? stored
      : null;
  });
  const [starting, setStarting] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [category, setCategory] = useState("Other");

  const socketRef = useRef(null);
  const typingTimer = useRef(null);
  const chatIdRef = useRef(chatId);

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);


  const loadHistory = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/chat/${id}/messages`);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error("[UserChatWidget] loadHistory:", err);
      setMessages([]);
    }
  }, []);

  const enterChat = useCallback((id) => {
    if (!id) return;
    safePersist(id);
    setChatId(id);
    chatIdRef.current = id;
    setView("chat");
  }, []);

  const resetToWelcome = useCallback(() => {
    clearPersisted();
    setChatId(null);
    chatIdRef.current = null;
    setMessages([]);
    setView("welcome");
  }, []);

  useEffect(() => {
    if (!user?._id || !token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      const currentId = chatIdRef.current;
      if (currentId) {
        socket.emit("user_reconnect", { chatId: currentId });
      }
    });

    socket.on("connect_error", (err) => {
      console.error("[UserChatWidget] socket connect_error:", err.message);
    });

    socket.on("chat_rejoined", ({ chatId: confirmedId }) => {
      enterChat(confirmedId);
      loadHistory(confirmedId);
    });

    socket.on("chat_not_found", () => {
      resetToWelcome();
    });

    socket.on("receive_message", (msg) => {
      const ours = chatIdRef.current?.toString();
      const theirs = msg.chatId?.toString() || msg.chat?._id?.toString();
      if (ours && theirs && theirs !== ours) return;

      setMessages((prev) => {
        if (msg._id) {
          const exists = prev.find(
            (m) => m._id?.toString() === msg._id.toString(),
          );
          if (exists) return prev;
        }
        const withoutTemp = msg.tempId
          ? prev.filter((m) => m.tempId !== msg.tempId)
          : prev;
        return [...withoutTemp, msg];
      });
    });

    socket.on("message_read", ({ messageId }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m._id?.toString() === messageId?.toString()
            ? { ...m, read: true }
            : m,
        ),
      );
    });

    socket.on("typing", ({ isTyping }) => {
      setAgentTyping(isTyping);
      clearTimeout(typingTimer.current);
      if (isTyping)
        typingTimer.current = setTimeout(() => setAgentTyping(false), 4000);
    });

    return () => {
      socket.disconnect();
      clearTimeout(typingTimer.current);
    };
  }, [user?._id, token, enterChat, loadHistory, resetToWelcome]);

  useEffect(() => {
    if (!isOpen || !user?._id) return;

    const stored = chatIdRef.current;

    if (stored) {
      socketRef.current?.emit("user_reconnect", { chatId: stored });
      setView("chat");
    } else {
      (async () => {
        try {
          const res = await api.get("/chat/mine");
          const id = extractChatId(res.data);
          if (id) {
            enterChat(id);
            socketRef.current?.emit("join_room", { chatId: id });
            await loadHistory(id);
          }
        } catch {
          // 404 = no active session; stay on welcome screen
        }
      })();
    }
  }, [isOpen, user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view === "chat" && chatId) {
      loadHistory(chatId);
    }
  }, [view, chatId, loadHistory]);

  const handleStart = useCallback(async () => {
    if (!user?._id) return;
    setStarting(true);
    try {
      const res = await api.post("/chat", {
        subject: "Support Request",
        category,
      });
      const newId = extractChatId(res.data);
      if (!newId) throw new Error("Server did not return a valid chatId");

      enterChat(newId);
      socketRef.current?.emit("join_room", { chatId: newId });
    } catch (err) {
      console.error("[UserChatWidget] handleStart:", err);
    } finally {
      setStarting(false);
    }
  }, [enterChat, user?._id, category]);

  const handleSend = useCallback((text) => {
    if (!chatIdRef.current || !text.trim()) return;
    const tempId = `tmp-${Date.now()}`;

    setMessages((prev) => [
      ...prev,
      {
        tempId,
        text,
        senderType: "user",
        createdAt: new Date().toISOString(),
        read: false,
      },
    ]);

    socketRef.current?.emit("send_message", {
      chatId: chatIdRef.current,
      text,
      tempId,
    });
  }, []);

  const handleTyping = useCallback(() => {
    if (chatIdRef.current) {
      socketRef.current?.emit("typing", {
        chatId: chatIdRef.current,
        isTyping: true,
      });
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openChat: () => {
        setIsOpen(true);
        if (!chatIdRef.current) handleStart();
      },
      closeChat: () => setIsOpen(false),
    }),
    [handleStart],
  );

  if (!user || user.isAdmin) return null;

  return (
    <>
      <button
        className="fc-fab"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Support Chat"
      >
        <img
          src="/images/contact-us.png"
          alt="Support"
          className="fc-fab-img"
        />
        <span className="fc-fab-badge" />
      </button>

      {isOpen && (
        <div className="fc-overlay" onClick={() => setIsOpen(false)}>
          <div className="fc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fc-header">
              <div className="fc-header-left">
                {view === "chat" && (
                  <button
                    className="fc-back-btn"
                    onClick={() => setView("welcome")}
                  >
                    <FaChevronLeft /> Back
                  </button>
                )}
                <div className="fc-header-icon">
                  <FaHeadset />
                </div>
              </div>
              <button className="fc-close-btn" onClick={() => setIsOpen(false)}>
                ✕
              </button>
            </div>

            {view === "welcome" ? (
              <WelcomeScreen
                onStart={handleStart}
                loading={starting}
                firstName={user?.firstName}
                category={category}
                onCategoryChange={setCategory}
              />
            ) : (
              <ChatScreen
                messages={messages}
                onSend={handleSend}
                onTyping={handleTyping}
                agentName="Support"
                userAvatar={userAvatar}
                agentTyping={agentTyping}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
});

UserChatWidget.displayName = "UserChatWidget";

export default UserChatWidget;
